import { css } from '@emotion/react';
import {
  Colors,
  Field,
  Link,
  Text,
  TextInput,
  Typography,
} from '@zapier/design-system';
import { urls, useSize } from '@zapier/identity-utils';
import { Button } from '@zapier/marketing-ui';
import { addParamsToUrl } from '@zapier/url-utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import { Fragment } from 'react';

// By path: @zapier/authentication doesn't export these.
import PasswordRequirement from '@zapier/authentication/src/components/SignUpForm/PasswordRequirements/PasswordRequirement';
import PasswordRequirements from '@zapier/authentication/src/components/SignUpForm/PasswordRequirements';

import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';
import { parseHash } from '@zapier/auth-pages/src/components/pages/ResetPasswordPage/parseHash';
import { useResetPassword } from './useResetPassword';

const PASSWORD_INPUT_ID = 'reset-password-1';
const CONFIRM_PASSWORD_INPUT_ID = 'reset-password-2';
const REQUIREMENTS_ID = 'reset-password-requirements';
const FORM_STATUS_ID = 'reset-password-status';

/** Keeps Field's own description (help or error text) alongside ours. */
const inputDescribedBy = (fieldDescribedBy?: string) =>
  [fieldDescribedBy, REQUIREMENTS_ID, FORM_STATUS_ID].filter(Boolean).join(' ');

const errorStyle = css`
  ${Typography.base}
  color: ${Colors.StatusErrorWeaker};
`;

const rootStyle = css`
  display: grid;
  grid-gap: 25px;
`;

const requirementsStyle = css`
  display: grid;
  gap: var(--zds-space-8);
`;

/** Layout only; messages use StatusErrorStrongest, as Weaker fails the APCA body-text floor. */
const statusStyle = css`
  display: grid;
  gap: var(--zds-space-4);

  &:not(:empty) {
    margin-top: var(--zds-space-4);
  }
`;

/** Bridge until marketing-ui's Button supports a focusable unavailable state; stacked `&` beats its :hover. */
const unavailableSubmitStyle = css`
  &&&&[aria-disabled='true'] {
    color: var(--zds-text-disabled);
    background-color: var(--zds-background-strongest);
    border-color: var(--zds-background-strongest);
    cursor: not-allowed;
  }
`;

const successStyle = css`
  ${Typography.base}
  color: ${Colors.StatusSuccessWeaker};
`;

export function ResetPasswordPage() {
  const size = useSize();
  const router = useRouter();
  const t = useTranslations('reset-password');

  const hashPart = router.asPath.split('#')?.[1];
  const hash = (hashPart || router.query?.hash || '').toString();
  const { username } = parseHash(hash);

  const {
    canSubmit,
    confirmPasswordError,
    error,
    formData,
    handleChange,
    handleConfirmPasswordBlur,
    handleSubmit,
    passwordErrors,
    passwordsMatch,
    refusedSubmitCount,
    status,
    unmetRuleMessages,
  } = useResetPassword({ hash });

  const title = username
    ? t('title-with-username', { username })
    : t('title-default');

  function LoginLink(chunks: React.ReactNode) {
    return (
      <Link href={addParamsToUrl(urls.LOGIN_URL, { email: username })}>
        {chunks}
      </Link>
    );
  }

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={title}
      titlePosition={'right'}
    >
      <form css={rootStyle} onSubmit={handleSubmit}>
        {/* Before the fields; both inputs reference it (SC 3.3.2). */}
        <div css={requirementsStyle} id={REQUIREMENTS_ID}>
          <Text tag="p" type="Body3">
            {t('requirements-title')}
          </Text>
          <PasswordRequirements
            password={formData.password1}
            passwordErrors={passwordErrors}
            shouldAnnounceSummary={false}
          />
          <ul>
            <li>
              <PasswordRequirement
                testId="resetPasswordRequirementPasswordsMatch"
                requirementIsSatisfied={passwordsMatch}
              >
                {t('requirements-match')}
              </PasswordRequirement>
            </li>
          </ul>
        </div>

        <Field
          label={t('password-label')}
          inputId={PASSWORD_INPUT_ID}
          isRequired={true}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-describedby={inputDescribedBy(inputProps.ariaDescribedBy)}
              autoComplete="new-password"
              isDisabled={status === 'submitting'}
              name="password1"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleChange('password1', e.target.value)
              }
              placeholder={t('password-placeholder')}
              type="password"
              value={formData.password1}
            />
          )}
        />

        <div>
          <Field
            label={t('confirm-password-label')}
            inputId={CONFIRM_PASSWORD_INPUT_ID}
            isRequired={true}
            renderInput={inputProps => (
              <TextInput
                {...inputProps}
                aria-describedby={inputDescribedBy(inputProps.ariaDescribedBy)}
                autoComplete="new-password"
                isDisabled={status === 'submitting'}
                name="password2"
                onBlur={handleConfirmPasswordBlur}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange('password2', e.target.value)
                }
                placeholder={t('confirm-password-placeholder')}
                type="password"
                value={formData.password2}
              />
            )}
          />
          {/* Not Field's `error` prop (unassociated, breaks aria-errormessage); always mounted so AT observes it first. */}
          <div css={statusStyle} id={FORM_STATUS_ID} role="status">
            {/* key: re-announces on each refused submit. */}
            <Fragment key={refusedSubmitCount}>
              {unmetRuleMessages.length > 0 && (
                <>
                  <Text tag="p" type="Body3" color="StatusErrorStrongest">
                    {t('errors.rules-unmet')}
                  </Text>
                  {unmetRuleMessages.map(message => (
                    <Text
                      key={message}
                      tag="p"
                      type="Body3"
                      color="StatusErrorStrongest"
                    >
                      {message}
                    </Text>
                  ))}
                </>
              )}
              {confirmPasswordError && (
                <Text tag="p" type="Body3" color="StatusErrorStrongest">
                  {confirmPasswordError}
                </Text>
              )}
            </Fragment>
          </div>
        </div>
        {/* aria-disabled, not disabled: stays focusable so a refused press can explain itself. */}
        <Button
          type="submit"
          variant="brand"
          fullWidth={true}
          aria-describedby={FORM_STATUS_ID}
          aria-disabled={canSubmit ? undefined : true}
          css={unavailableSubmitStyle}
        >
          {t('submit-button')}
        </Button>
        {error && <div css={errorStyle}>{error}</div>}
        {status === 'success' && (
          <div css={successStyle}>{t.rich('success', { link: LoginLink })}</div>
        )}
      </form>
    </AuthLayout>
  );
}
